import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import styles from "./DashboardFilter.module.scss";
import {
  dashboardDateRangeOptions,
  getDashboardDateRange,
} from "../../containers/dashboard/dateRanges";
import {
  DashboardCountryOption,
  DashboardDateRange,
  DashboardFilters,
} from "../../containers/dashboard/types";

interface DashboardFilterProps {
  filter: DashboardFilters;
  regions: string[];
  countries: DashboardCountryOption[];
  screeningFamilies: string[];
  isApplying: boolean;
  onChange: React.Dispatch<React.SetStateAction<DashboardFilters>>;
  onApply: () => void;
}

const screeningFamilyLabels: Record<string, string> = {
  ALL: "All risk areas and queues",
  PV_PTQ: "PV + PTQ",
  PCO: "PCO",
};

const DashboardFilter = ({
  filter,
  regions,
  countries,
  screeningFamilies,
  isApplying,
  onChange,
  onApply,
}: DashboardFilterProps) => {
  const availableCountries = countries.filter(
    (country) => filter.region === "ALL" || country.region === filter.region,
  );
  const familyOptions = screeningFamilies.length ? screeningFamilies : ["ALL"];

  const handleDateRangeChange = (event: SelectChangeEvent) => {
    const dateRange = event.target.value as DashboardDateRange;
    const [startDate, endDate] = getDashboardDateRange(dateRange);
    onChange({ ...filter, dateRange, startDate, endDate });
  };

  const handleRegionChange = (event: SelectChangeEvent) => {
    onChange({ ...filter, region: event.target.value, country: "ALL" });
  };

  return (
    <Box className={styles.filterWrapper} aria-label="Dashboard filters">
      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-date-range-label">Date range</InputLabel>
        <Select
          labelId="dashboard-date-range-label"
          id="dashboard-date-range"
          value={filter.dateRange}
          label="Date range"
          onChange={handleDateRangeChange}
        >
          {dashboardDateRangeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-region-label">Region</InputLabel>
        <Select
          labelId="dashboard-region-label"
          id="dashboard-region"
          value={filter.region}
          label="Region"
          onChange={handleRegionChange}
        >
          <MenuItem value="ALL">All regions</MenuItem>
          {regions.map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-country-label">Country</InputLabel>
        <Select
          labelId="dashboard-country-label"
          id="dashboard-country"
          value={filter.country}
          label="Country"
          onChange={(event) =>
            onChange({ ...filter, country: event.target.value })
          }
        >
          <MenuItem value="ALL">All countries</MenuItem>
          {availableCountries.map((country) => (
            <MenuItem
              key={`${country.region}-${country.name}`}
              value={country.name}
            >
              {country.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="dashboard-screening-family-label">
          Risk area or screening queue
        </InputLabel>
        <Select
          labelId="dashboard-screening-family-label"
          id="dashboard-screening-family"
          value={filter.screeningFamily}
          label="Risk area or screening queue"
          onChange={(event) =>
            onChange({
              ...filter,
              screeningFamily: event.target.value,
              country: "ALL",
            })
          }
        >
          {familyOptions.map((family) => (
            <MenuItem key={family} value={family}>
              {screeningFamilyLabels[family] ?? family}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        className={styles.applyButton}
        variant="contained"
        onClick={onApply}
        disabled={isApplying}
      >
        Apply filters
      </Button>
    </Box>
  );
};

export default DashboardFilter;
